import { useCallback, useEffect, useState } from 'react';
import { FaBarcode, FaCommentDots, FaPlus } from 'react-icons/fa';

import SideDrawer from '../../../organisms/SideDrawer';
import BadgeStatus from '../../../atoms/BadgeStatus';
import MiniLoading from '../../../ions/MiniLoading';
import InvoiceDetailsContent, { InvoiceAdditionalData } from '../modals/InvoiceDetailsContent';
import InvoiceBillingEventsPanel from '../modals/InvoiceBillingEventsPanel';
import NewInvoiceBillingEventModal from '../modals/NewInvoiceBillingEventModal';
import BoletoModal from '../modals/BoletoModal';
import AddInvoiceCommentModal from '../modals/AddInvoiceCommentModal';
import { getInvoiceById } from '../services/billingApi';
import { invoiceStatusColor } from './invoiceStatusColors';

const OVER_DRAWER = 'z-[10000]';
const OVER_DRAWER_CONFIRM = 'z-[10001]';

const PRIMARY_HEADER_BUTTON = 'inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-blue-600 text-white '
    + 'hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400';

const HEADER_BUTTON = 'inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-gray-600 text-white '
    + 'hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400';

interface Props {
    invoiceId: string;
    initialInvoice?: any;
    onClose: () => void;
    onChanged?: () => void;
}

type LoadState = 'loading' | 'loaded' | 'not-found' | 'failed';

const subtitleOf = (invoice: any): string | undefined => {
    if (!invoice) return undefined;
    return [
        invoice.operationNumber && `Operação ${invoice.operationNumber}`,
        invoice.details?.documentTitle && `Título/NF ${invoice.details.documentTitle}`,
        invoice.payer?.name,
    ].filter(Boolean).join(' · ') || undefined;
};

const InvoiceDrawer: React.FC<Props> = ({ invoiceId, initialInvoice, onClose, onChanged }) => {
    const [invoice, setInvoice] = useState<any>(initialInvoice ?? null);
    const [state, setState] = useState<LoadState>(initialInvoice ? 'loaded' : 'loading');
    const [showBoleto, setShowBoleto] = useState(false);
    const [commenting, setCommenting] = useState(false);
    const [addingEvent, setAddingEvent] = useState(false);
    const [eventsReloadKey, setEventsReloadKey] = useState(0);

    const load = useCallback(async () => {
        try {
            const found = await getInvoiceById(invoiceId);
            setInvoice(found);
            setState(found ? 'loaded' : 'not-found');
        } catch {
            setState(current => (current === 'loaded' ? current : 'failed'));
        }
    }, [invoiceId]);

    useEffect(() => { load(); }, [load]);

    const afterChange = () => {
        load();
        onChanged?.();
    };

    return (
        <>
            <SideDrawer
                open
                title={invoice ? `Fatura ${invoice.invoiceId?.slice(0, 8) ?? ''}` : 'Fatura'}
                subtitle={subtitleOf(invoice)}
                width="920px"
                onClose={onClose}
                titleAccessory={invoice && (
                    <BadgeStatus text={invoice.statusFormatted || invoice.status}
                        color={invoiceStatusColor(invoice.status)} />
                )}
                headerActions={invoice && (
                    <>
                        <button type="button" className={PRIMARY_HEADER_BUTTON} onClick={() => setAddingEvent(true)}>
                            <FaPlus /> Adicionar evento
                        </button>
                        <button type="button" className={HEADER_BUTTON} onClick={() => setCommenting(true)}>
                            <FaCommentDots /> Comentário
                        </button>
                        {invoice.boleto && (
                            <button type="button" className={HEADER_BUTTON} onClick={() => setShowBoleto(true)}>
                                <FaBarcode /> Boleto
                            </button>
                        )}
                    </>
                )}
            >
                {state === 'loading' && (
                    <p className="flex items-center gap-2 text-sm text-gray-500"><MiniLoading /> Carregando fatura...</p>
                )}
                {state === 'not-found' && <p className="text-sm text-gray-500">Fatura não encontrada.</p>}
                {state === 'failed' && (
                    <div className="text-sm">
                        <p className="text-red-600">Não foi possível carregar a fatura.</p>
                        <button type="button" onClick={load} className="mt-2 text-blue-600 hover:underline">Tentar de novo</button>
                    </div>
                )}
                {state === 'loaded' && invoice && (
                    <>
                        <InvoiceDetailsContent invoice={invoice} showAdditionalData={false} />
                        <section className="p-3 rounded-lg border border-gray-200 dark:border-slate-600">
                            <InvoiceBillingEventsPanel invoiceRef={invoice.invoiceId} onChanged={afterChange}
                                title="Eventos de cobrança" hideAddButton reloadKey={eventsReloadKey}
                                zIndexClass={OVER_DRAWER} confirmZIndexClass={OVER_DRAWER_CONFIRM} />
                        </section>
                        <div className="mt-4"><InvoiceAdditionalData invoice={invoice} /></div>
                    </>
                )}
            </SideDrawer>
            {showBoleto && invoice && (
                <BoletoModal invoice={invoice} zIndexClass={OVER_DRAWER}
                    onClose={() => setShowBoleto(false)} onChanged={afterChange} />
            )}
            {addingEvent && invoice && (
                <NewInvoiceBillingEventModal invoiceRef={invoice.invoiceId} zIndexClass={OVER_DRAWER}
                    onClose={() => setAddingEvent(false)}
                    onCreated={() => {
                        setAddingEvent(false);
                        setEventsReloadKey(k => k + 1);
                        afterChange();
                    }} />
            )}
            {commenting && invoice && (
                <AddInvoiceCommentModal invoice={invoice} zIndexClass={OVER_DRAWER}
                    onClose={() => setCommenting(false)} onAdded={afterChange} />
            )}
        </>
    );
};

export default InvoiceDrawer;
